/**
 * Status visuals (DESIGN 6.3 "Status colors", pillar 4 "every status has an
 * unmistakable visual"): wet drips + blue tint, burning flames (one flame
 * per stack) + embers, chilled light-blue tint + frost, frozen ice block +
 * desaturation + stillness, shocked jitter + purple sparks, knockdown
 * rotated body, hasted speed lines, slowed grey clock ring.
 *
 * `statusLook` folds a status list into a reusable StatusLook that the body
 * drawers consult (tint, jitter, lying, animation rate); the draw / emit
 * helpers add the overlays around an already-drawn body.
 */

import { STATUS_DURATION, type StatusId, type StatusSnap } from '@shared/index.js';
import { ELEMENT_SWATCH, greyed, mixHex, rgba, STATUS_COLORS, UI } from '../palette.js';
import { hashRand, polygonPath, TAU, type Ctx } from './gfx.js';
import { drawStatusGlyph, drawTimeArc } from './glyphs.js';
import { PC, type Particles } from './particles.js';
import type { Tint } from './sprites.js';
import { drawBodyOverlay, drawBurnArt, drawFrozenArt, fxAt, notFrozen } from './spellArt.js';

export interface StatusLook {
  wet: boolean;
  burning: number;
  chilled: boolean;
  frozen: boolean;
  shocked: boolean;
  knockdown: boolean;
  hasted: boolean;
  slowed: boolean;
  /** Stuck in mud. */
  mired: boolean;
  /** Seconds left on knockdown (for the stand-up tween) and frozen (for the thaw flicker). */
  knockdownT: number;
  frozenT: number;
  /** Body jitter in px (shocked). */
  jx: number;
  jy: number;
  /** Animation rate multiplier: 0 frozen, 0.6 chilled, 0.5 slowed, 2 hasted. */
  anim: number;
}

export function emptyLook(): StatusLook {
  return { wet: false, burning: 0, chilled: false, frozen: false, shocked: false, knockdown: false, hasted: false, slowed: false, mired: false, knockdownT: 0, frozenT: 0, jx: 0, jy: 0, anim: 1 };
}

/** Fill `out` from a status list. `time` seconds and `id` seed the shock jitter. */
export function statusLook(st: readonly StatusSnap[], time: number, id: number, out: StatusLook): StatusLook {
  out.wet = false; out.burning = 0; out.chilled = false; out.frozen = false; out.shocked = false;
  out.knockdown = false; out.hasted = false; out.slowed = false; out.mired = false; out.knockdownT = 0; out.frozenT = 0;
  out.jx = 0; out.jy = 0; out.anim = 1;
  for (const s of st) {
    switch (s.id) {
      case 'wet': out.wet = true; break;
      case 'burning': out.burning = Math.max(1, s.s); break;
      case 'chilled': out.chilled = true; break;
      case 'frozen': out.frozen = true; out.frozenT = s.t; break;
      case 'shocked': out.shocked = true; break;
      case 'knockdown': out.knockdown = true; out.knockdownT = s.t; break;
      case 'hasted': out.hasted = true; break;
      case 'slowed': out.slowed = true; break;
      case 'mired': out.mired = true; break;
    }
  }
  if (out.shocked) {
    const frame = Math.floor(time * 30);
    out.jx = (hashRand(id, frame) - 0.5) * 4;
    out.jy = (hashRand(id + 1, frame) - 0.5) * 4;
  }
  if (out.frozen || out.shocked) out.anim = 0;
  else if (out.chilled) out.anim = 0.6;
  else if (out.slowed) out.anim = 0.5;
  else if (out.mired) out.anim = 0.45;
  else if (out.hasted) out.anim = 2;
  return out;
}

/** The same tints as washes over a sprite body (frozen pales to ice, chilled cools, burning warms, wet cools, a hit whitens). */
export function statusTints(look: StatusLook, flash: number): Tint[] {
  const tints: Tint[] = [];
  if (look.frozen) tints.push({ color: ELEMENT_SWATCH.ice.core, alpha: 0.5 });
  else if (look.chilled) tints.push({ color: ELEMENT_SWATCH.cold.core, alpha: 0.3 });
  if (look.burning > 0) tints.push({ color: ELEMENT_SWATCH.fire.core, alpha: 0.2 });
  else if (look.wet && !look.frozen) tints.push({ color: ELEMENT_SWATCH.water.core, alpha: 0.2 });
  if (flash > 0) tints.push({ color: '#FFFFFF', alpha: flash });
  return tints;
}

/** Body colour after status tints: frozen desaturates, chilled pales, burning warms, wet cools slightly. */
export function tintBody(base: string, look: StatusLook, flash: number): string {
  let c = base;
  if (look.frozen) c = greyed(c, 0.6);
  else if (look.chilled) c = mixHex(c, ELEMENT_SWATCH.cold.core, 0.3); // pale but still a silhouette
  if (look.burning > 0) c = mixHex(c, ELEMENT_SWATCH.fire.core, 0.2);
  else if (look.wet && !look.frozen) c = mixHex(c, ELEMENT_SWATCH.water.core, 0.2);
  if (flash > 0) c = mixHex(c, '#FFFFFF', flash);
  return c;
}

/**
 * Ground-level status marks drawn before the body: wet puddle, slowed clock
 * ring, frozen ice base. (px, py) = feet on screen; r = body radius in px.
 */
export function drawStatusBelow(ctx: Ctx, px: number, py: number, r: number, look: StatusLook, time: number): void {
  const wetArt = look.wet && !look.frozen && fxAt(ctx, 'fx-status', 'wet', time + px * 0.01, px, py + 1);
  const mudArt = look.mired && fxAt(ctx, 'fx-status', 'mired', time + px * 0.013, px, py + 1);
  if (look.wet && !look.frozen && !wetArt) {
    ctx.fillStyle = rgba(ELEMENT_SWATCH.water.core, 0.22);
    ctx.beginPath();
    ctx.ellipse(px, py + 2, r * 1.3, r * 0.5, 0, 0, TAU);
    ctx.fill();
  }
  if (look.mired && !mudArt) {
    // A lumpy mud patch the body is sunk into: three overlapping blobs, slowly heaving.
    ctx.fillStyle = rgba(STATUS_COLORS.mired, 0.85);
    for (let i = 0; i < 3; i++) {
      const ox = (i - 1) * r * 0.7;
      const wob = 1 + 0.08 * Math.sin(time * 2 + i * 2.1);
      ctx.beginPath();
      ctx.ellipse(px + ox, py + 1, r * 0.75 * wob, r * 0.32 * wob, 0, 0, TAU);
      ctx.fill();
    }
  }
  if (look.slowed) {
    ctx.strokeStyle = rgba(STATUS_COLORS.slowed, 0.8);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(px, py, r * 1.6, r * 0.65, 0, 0, TAU);
    ctx.stroke();
    // a sweeping clock hand
    const a = (time * 0.8) % TAU;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px + Math.cos(a) * r * 1.6, py + Math.sin(a) * r * 0.65);
    ctx.stroke();
  }
  if (look.frozen) {
    ctx.fillStyle = rgba(ELEMENT_SWATCH.ice.core, 0.35);
    ctx.beginPath();
    ctx.ellipse(px, py + 1, r * 1.5, r * 0.6, 0, 0, TAU);
    ctx.fill();
  }
}

/**
 * Overlays drawn after the body: flames by stack count, ice block, shock
 * sparks, speed lines, chilled rim. `h` = body height in px; `id` keeps a
 * body's ice block forming from the moment it froze. The effects library's
 * overlays draw when they have landed, the vector ones otherwise.
 */
export function drawStatusAbove(ctx: Ctx, px: number, py: number, r: number, h: number, look: StatusLook, time: number, facing: number, id = -1): void {
  if (!look.frozen) notFrozen(id);
  if (look.burning > 0 && !drawBurnArt(ctx, px, py, r, h, look.burning, time)) {
    // One flame per stack along the body; flicker by time.
    ctx.globalCompositeOperation = 'lighter';
    const n = Math.min(6, look.burning);
    for (let i = 0; i < n; i++) {
      const fx = px + ((i % 3) - 1) * r * 0.55 + Math.sin(time * 11 + i * 2.1) * 1.5;
      const fy = py - h * (0.25 + 0.2 * Math.floor(i / 3)) - (i % 2) * 3;
      const fh = r * (0.9 + 0.3 * Math.sin(time * 14 + i));
      const fw = r * 0.35;
      ctx.fillStyle = rgba(ELEMENT_SWATCH.fire.core, 0.85);
      ctx.beginPath();
      ctx.moveTo(fx, fy - fh);
      ctx.quadraticCurveTo(fx + fw * 1.2, fy - fh * 0.4, fx + fw, fy);
      ctx.lineTo(fx - fw, fy);
      ctx.quadraticCurveTo(fx - fw * 1.2, fy - fh * 0.4, fx, fy - fh);
      ctx.fill();
      ctx.fillStyle = rgba(ELEMENT_SWATCH.fire.soft, 0.9);
      ctx.beginPath();
      ctx.moveTo(fx, fy - fh * 0.5);
      ctx.lineTo(fx + fw * 0.45, fy);
      ctx.lineTo(fx - fw * 0.45, fy);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  }
  if (look.chilled && !look.frozen && !drawBodyOverlay(ctx, 'chill', px, py, h, time)) {
    ctx.strokeStyle = rgba(ELEMENT_SWATCH.cold.core, 0.8);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(px, py - h * 0.45, r * 1.15, h * 0.55, 0, 0, TAU);
    ctx.stroke();
  }
  const thaw = look.frozenT < 1 ? (Math.sin(time * 30) > 0 ? 0.6 : 1) : 1;
  if (look.frozen && !drawFrozenArt(ctx, id, px, py, h, thaw, time)) {
    // Translucent ice block: hexagonal prism around the body, facet lines, thaw flicker in the last second.
    ctx.globalAlpha = 0.55 * thaw;
    ctx.fillStyle = ELEMENT_SWATCH.ice.core;
    const cy = py - h * 0.5;
    polygonPath(ctx, px, cy, Math.max(r * 1.45, h * 0.62), 6, Math.PI / 6);
    ctx.fill();
    ctx.globalAlpha = 0.9 * thaw;
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(px - r * 0.8, cy - h * 0.35);
    ctx.lineTo(px - r * 0.2, cy - h * 0.05);
    ctx.lineTo(px + r * 0.6, cy - h * 0.3);
    ctx.moveTo(px - r * 0.2, cy - h * 0.05);
    ctx.lineTo(px + r * 0.1, cy + h * 0.4);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  if (look.shocked && !drawBodyOverlay(ctx, 'shock', px, py, h, time)) {
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = ELEMENT_SWATCH.lightning.core;
    ctx.lineWidth = 1.5;
    const frame = Math.floor(time * 24);
    ctx.beginPath();
    let x = px - r, y = py - h * 0.2;
    ctx.moveTo(x, y);
    for (let i = 1; i <= 5; i++) {
      x = px - r + (2 * r * i) / 5;
      y = py - h * (0.2 + hashRand(frame, i) * 0.7);
      ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  }
  if (look.hasted) {
    // speed lines trailing opposite the facing direction
    const dx = -Math.cos(facing), dy = -Math.sin(facing) * 0.4;
    ctx.strokeStyle = rgba(STATUS_COLORS.hasted, 0.7);
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < 3; i++) {
      const yy = py - h * (0.25 + i * 0.25);
      const phase = (time * 6 + i) % 1;
      const x0 = px + dx * r * (1.1 + phase * 0.6);
      ctx.moveTo(x0, yy + dy * r);
      ctx.lineTo(x0 + dx * r * 0.9, yy + dy * r * 2);
    }
    ctx.stroke();
  }
}

let fxSeed = 99;
function rnd(): number {
  fxSeed = (fxSeed * 1664525 + 1013904223) >>> 0;
  return fxSeed / 4294967296;
}

/** Stochastic per-frame particle emission for a body with these statuses (rates in events/s). */
export function emitStatusParticles(particles: Particles, x: number, y: number, facing: number, look: StatusLook, dt: number): void {
  if (look.wet && !look.frozen && rnd() < dt * 4) particles.drip(x, y, 0.4 + rnd() * 0.5);
  if (look.burning > 0 && rnd() < dt * (6 + look.burning * 3)) particles.embers(x, y, 0.3, 1);
  if (look.chilled && !look.frozen && rnd() < dt * 4) particles.frost(x, y, 0.2, 1);
  if (look.frozen && rnd() < dt * 1.5) particles.frost(x, y, 0.5, 1);
  if (look.shocked && rnd() < dt * 40) particles.sparks(x, y, 0.4, 1);
  if (look.hasted && rnd() < dt * 10) particles.streaks(PC.gold, x, y, 0.3, facing + Math.PI, 1, 3);
}

const ICON_ORDER: readonly StatusId[] = ['frozen', 'burning', 'wet', 'chilled', 'shocked', 'knockdown', 'mired', 'hasted', 'slowed'];

/** Row of status icons with duration arcs, centred on (px, py). Returns the row width. */
export function drawStatusIcons(ctx: Ctx, st: readonly StatusSnap[], px: number, py: number, size: number): number {
  if (st.length === 0) return 0;
  const gap = size * 1.35;
  let n = 0;
  for (const id of ICON_ORDER) if (st.some((s) => s.id === id)) n++;
  const x0 = px - ((n - 1) * gap) / 2;
  let i = 0;
  for (const id of ICON_ORDER) {
    const s = st.find((q) => q.id === id);
    if (!s) continue;
    const x = x0 + i * gap;
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.beginPath();
    ctx.arc(x, py, size * 0.72, 0, TAU);
    ctx.fill();
    ctx.fillStyle = STATUS_COLORS[id];
    drawStatusGlyph(ctx, id, x, py, size * 0.85);
    const dur = STATUS_DURATION[id];
    drawTimeArc(ctx, x, py, size * 0.72, dur > 0 ? s.t / dur : 0, STATUS_COLORS[id], 1.5);
    if (id === 'burning' && s.s > 1) {
      ctx.fillStyle = UI.white;
      ctx.font = `700 ${Math.round(size * 0.6)}px system-ui, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${s.s}`, x + size * 0.55, py - size * 0.45);
    }
    i++;
  }
  return n * gap;
}
